import { cachedLoader, type Lang } from '@fakerpg/core';
import type { Messages } from './fr.js';

/** Each UI language is its own chunk: a player only downloads the one they read. */
const loaders: Record<Lang, () => Promise<Messages>> = {
  fr: () => import('./fr.js').then((m) => m.fr),
  en: () => import('./en.js').then((m) => m.en),
};
const dictionaries: Partial<Record<Lang, Messages>> = {};
// A failed chunk is forgotten, so the next call retries.
const fetchLang = cachedLoader((lang: Lang) =>
  loaders[lang]().then((d) => {
    dictionaries[lang] = d;
  }),
);

/** Loads a UI language once; resolves at once when it is already there. */
export function loadLang(lang: Lang): Promise<void> {
  return dictionaries[lang] ? Promise.resolve() : fetchLang(lang);
}

export function isLangLoaded(lang: Lang): boolean {
  return !!dictionaries[lang];
}

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];
export type MessageKey = Leaves<Messages>;
export type Vars = Record<string, string | number>;

export function translate(lang: Lang, key: MessageKey, vars?: Vars): string {
  // Until a newly picked language arrives, show whichever one is loaded rather than raw keys.
  let node: unknown = dictionaries[lang] ?? dictionaries.fr ?? dictionaries.en;
  for (const part of key.split('.')) node = (node as Record<string, unknown> | undefined)?.[part];
  let text = typeof node === 'string' ? node : key;
  if (vars) text = text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
  return text;
}

export function detectLang(): Lang {
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'fr';
  // Spanish browsers used to get the English UI already; everything else stays French.
  return nav === 'en' || nav === 'es' ? 'en' : 'fr';
}

const locales: Record<Lang, string> = { fr: 'fr-FR', en: 'en-GB' };

export function formatNumber(lang: Lang, n: number): string {
  return new Intl.NumberFormat(locales[lang], { maximumFractionDigits: 2 }).format(n);
}

/** Compact for big stats: 12,3 k / 4,5 M. */
export function formatCompact(lang: Lang, n: number): string {
  if (Math.abs(n) < 10_000) return formatNumber(lang, Math.floor(n));
  return new Intl.NumberFormat(locales[lang], { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function formatRelative(lang: Lang, at: number, now: number): string {
  const diff = at - now;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locales[lang], { numeric: 'auto' });
  if (abs < 60_000) return rtf.format(Math.round(diff / 1000), 'second');
  if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (abs < 86_400_000) {
    const h = Math.floor(abs / 3_600_000);
    const m = Math.floor((abs % 3_600_000) / 60_000);
    return diff >= 0 ? `${rtf.format(h, 'hour')}${m ? ` ${m} min` : ''}` : rtf.format(-h, 'hour');
  }
  return rtf.format(Math.round(diff / 86_400_000), 'day');
}

/** "15 min", "2 h", "24 h". */
export function formatHours(hours: number): string {
  return hours < 1 ? `${Math.round(hours * 60)} min` : `${hours} h`;
}

export function formatClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

/** "31 October" / "31 octobre" for a month (0-based) and day. */
export function formatDayMonth(lang: Lang, month: number, day: number): string {
  return new Intl.DateTimeFormat(locales[lang], { day: 'numeric', month: 'long' }).format(new Date(2000, month, day));
}

/** "3 October 2026" / "3 octobre 2026". */
export function formatDate(lang: Lang, at: number): string {
  return new Intl.DateTimeFormat(locales[lang], { dateStyle: 'long' }).format(at);
}

/**
 * A refusal's message. A code this build does not know (a server deployed
 * ahead of the cached client) gets the generic "not possible now" text
 * instead of the raw key.
 */
export function errorText(lang: Lang, code: string, vars?: Vars): string {
  const key = `errors.${code}`;
  const text = translate(lang, key as MessageKey, vars);
  return text === key ? translate(lang, 'errors.wrongPhase') : text;
}
